import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/authContext';
import { useEstadoApp } from '../context/EstadoAppContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import ListaContactosProyecto from '../components/ListaContactosProyecto';
import { Modal } from '../components/ui/Modal';
import { Input, Label, Select_simple } from '../components/ui';
import { coincideContacto, normalizarTipoSubsidio } from '../utils/contactos';

// -----------------------------------------------------------------------------
// Administrar contactos de proyecto.
//
// Tiene dos modos, según el filtro de tipo de subsidio:
//
//  - ONG: el catálogo es por ONG. Se elige una ONG y se edita su lista. Además
//    se listan sus obras, porque un contacto que alguna obra sigue usando no se
//    puede dar de baja, y sin ver las obras no hay forma de saber cuál lo
//    bloquea. Con doble click se abre la obra en /inicio.
//
//  - Municipio / Universidades / Otro: el catálogo es por tipo_subsidio +
//    partido (migración 066). Se elige un partido y se edita la lista de ese
//    partido.
//
// QUE EDITA Y QUE NO: en modo ONG solo se edita la lista de contactos de la
// organización. Los datos de la ONG (código, partido, resolución, LPO,
// referente) se muestran como referencia y se editan en Nueva ONG. Editar la
// misma ONG desde dos pantallas es como se termina teniendo dos versiones de lo
// mismo. En modo partido solo se edita la lista de contactos.
//
// OJO con el doble click: InicioPage solo lee el ?id_partido_obra= de la URL
// cuando el estado de la app es edit_desde_pagos, es decir que navegar sola NO
// alcanza: hay que setear localStorage y el estado antes, como hacen TablaObras
// y ChatBurbujaGlobal. Las obras dadas de baja NO se abren.
// -----------------------------------------------------------------------------

const TIPO_ONG = 'Subsidio a ONG';

const OPCIONES_TIPO = [
    { value: 'Subsidio a ONG', label: 'Subsidio a ONG' },
    { value: 'Subsidio a municipio', label: 'Subsidio a municipio' },
    { value: 'Subsidio a Universidades', label: 'Subsidio a Universidades' },
    { value: 'Otro', label: 'Otro' }
];

// Nombre corto para la tabla de métricas de la cabecera.
const etiquetaTipo = (tipo) => ({
    'Subsidio a ONG': 'ONGs',
    'Subsidio a municipio': 'Municipios',
    'Subsidio a Universidades': 'Universidades',
    'Otro': 'Otro'
}[tipo] || tipo);

// Cómo se nombra el scope en el mensaje de lista vacía. La lista no es la de
// una obra, es la del catálogo del tipo: "para esta obra" no aplica.
const nombreScope = (tipo) => {
    if (tipo === 'Subsidio a municipio') return 'este Municipio';
    if (tipo === 'Subsidio a Universidades') return 'esta Universidad';
    return 'este organismo';
};

// -----------------------------------------------------------------------------
// Datos de contacto en una linea, para el modal de impacto.
// -----------------------------------------------------------------------------
const etiquetaContactoAfectado = (a) => (a?.nombre || '').trim() || `contacto ${a?.contacto_id ?? '?'}`;

// Dato de referencia: se muestra, no se toca.
const Dato = ({ etiqueta, valor }) => (
    <div className="min-w-0">
        <dt className="text-xs text-gray-500 dark:text-gray-400">{etiqueta}</dt>
        <dd className="text-sm text-gray-800 dark:text-gray-100 break-words">
            {valor === null || valor === undefined || valor === '' ? '—' : valor}
        </dd>
    </div>
);

// Firma de la lista, para saber si hay cambios sin guardar. Solo mira los
// campos que se editan: el contacto guardado trae también 'origen', así que
// comparar el objeto entero daría "cambios sin guardar" apenas se abre.
const firmaContactos = (lista) => JSON.stringify(
    (Array.isArray(lista) ? lista : [])
        .map(c => [c.contacto_id || 0, c.nombre || '', c.telefono || '', c.email || ''])
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
);

const ContactosONGPage = () => {
    const { token } = useContext(AuthContext);
    const { cambiarEstadoApp } = useEstadoApp();
    const { setItem } = useLocalStorage();
    const navigate = useNavigate();
    const location = useLocation();

    // Sin tipo seleccionado al entrar: no se carga ni se muestra ninguna lista
    // hasta que el usuario elija un tipo (en el select o en la tabla de
    // metricas de arriba).
    const [tipoFiltro, setTipoFiltro] = useState('');
    const esModoONG = tipoFiltro === TIPO_ONG;
    const hayTipo = Boolean(tipoFiltro);

    // Modo ONG
    const [ongs, setOngs] = useState([]);
    const [seleccionId, setSeleccionId] = useState(null);
    const [detalle, setDetalle] = useState(null);
    const [obras, setObras] = useState([]);
    const [cargandoObras, setCargandoObras] = useState(false);
    const [avisoObras, setAvisoObras] = useState('');
    // Checkbox de la lista de obras: ver solo las que tienen contactos.
    const [soloConContactos, setSoloConContactos] = useState(false);

    // Modo partido
    const [partidos, setPartidos] = useState([]);
    const [partidoSel, setPartidoSel] = useState(null);

    // Metricas por tipo, para la cabecera (se cargan una vez y se refrescan al
    // guardar, no dependen del tipo seleccionado).
    const [metricas, setMetricas] = useState([]);

    // Modal de carga de contactos para cualquier partido, aunque no tenga obras
    // de ese tipo. La lista izquierda solo muestra partidos con obras (o con
    // contactos ya cargados); el modal accede a los 145.
    const [modalCarga, setModalCarga] = useState(false);
    const [partidosTodos, setPartidosTodos] = useState([]);
    const [partidoModal, setPartidoModal] = useState('');
    const [contactosModal, setContactosModal] = useState([]);
    const [cargandoModal, setCargandoModal] = useState(false);
    const [guardandoModal, setGuardandoModal] = useState(false);
    const [mensajeModal, setMensajeModal] = useState(null);
    // Linea base y pendiente del modal, mismo criterio que la pagina.
    const [baselineModal, setBaselineModal] = useState([]);

    // Ref a la lista montada (ONG o partido) y al editor del modal, para poder
    // agregar lo que quedo tipeado sin apretar "Agregar" antes de guardar.
    const listaRef = useRef(null);
    const listaModalRef = useRef(null);

    // Linea base de lo guardado en modo partido, para saber si hay cambios: en
    // ONG la da detalle.contactos, que ya viene del servidor. Sin esto, el
    // guardado estaba siempre habilitado aunque no hubiera nada que guardar.
    const [baselinePartido, setBaselinePartido] = useState([]);
    // El modal de impacto: que se va a guardar y en que obras va a pegar. Se
    // arma antes del PUT, no despues, para poder cancelar.
    const [impactoPendiente, setImpactoPendiente] = useState(null);

    // Comunes
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [busqueda, setBusqueda] = useState('');
    // Buscador de contactos, aparte del de ONGs/partidos. Se combinan: escribir
    // en los dos filtra por ambos. Pisar un contacto con el celu es distinto de
    // acordarse de la ONG, asi que hace falta un campo propio.
    const [busquedaContacto, setBusquedaContacto] = useState('');
    const [filtro, setFiltro] = useState('todas');
    const [cargandoDetalle, setCargandoDetalle] = useState(false);
    const [borrador, setBorrador] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [mensaje, setMensaje] = useState(null);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }), [token]);

    useEffect(() => {
        cambiarEstadoApp('inicial');
    }, []);

    // Al volver de editar una obra (doble click), se reabre el mismo panel en
    // vez de dejar la seleccion vacia, y se avisa que se guardo. En modo ONG el
    // id viaja en ?id_ong=; en modo partido, el tipo y el partido. Los dos son
    // lo unico que sobrevive al desmontaje del panel.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('edicionExitosa')) {
            setAvisoObras('Se guardó la obra.');
        }

        const id = parseInt(params.get('id_ong'), 10);
        if (!Number.isNaN(id)) {
            setSeleccionId(id);
            setCargandoDetalle(true);
            setCargandoObras(true);
            cargarDetalle(id);
            cargarObras(id);
            navigate(location.pathname, { replace: true });
            return;
        }

        const tipo = normalizarTipoSubsidio(params.get('tipo_subsidio'));
        const partido = (params.get('partido') || '').trim();
        if (tipo && tipo !== TIPO_ONG && partido) {
            setTipoFiltro(tipo);
            setPartidoSel(partido);
            setCargandoDetalle(true);
            setCargandoObras(true);
            cargarContactosPartido(partido, tipo);
            cargarObrasPartido(partido, tipo);
            navigate(location.pathname, { replace: true });
        }
        // Solo al montar: es la vuelta de la edicion, no un filtro de la pagina.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cargarLista = useCallback(async () => {
        // Sin tipo seleccionado no hay nada que traer: la lista no se muestra.
        if (!hayTipo) {
            setOngs([]);
            setPartidos([]);
            setCargando(false);
            return;
        }
        setCargando(true);
        setError('');
        try {
            if (esModoONG) {
                const r = await fetch('/api/ongs', { headers });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (!Array.isArray(data)) throw new Error('Respuesta inválida');
                setOngs(data);
            } else {
                const r = await fetch(`/api/contactos-proyecto/resumen?tipo_subsidio=${encodeURIComponent(tipoFiltro)}`, { headers });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (!Array.isArray(data)) throw new Error('Respuesta inválida');
                setPartidos(data);
            }
        } catch (e) {
            console.error('Error al cargar la lista:', e);
            setError(esModoONG ? 'No se pudieron cargar las ONGs' : 'No se pudieron cargar los partidos');
        } finally {
            setCargando(false);
        }
    }, [headers, esModoONG, tipoFiltro, hayTipo]);

    useEffect(() => { cargarLista(); }, [cargarLista]);

    const cargarMetricas = useCallback(async () => {
        try {
            const r = await fetch('/api/contactos-proyecto/metricas', { headers });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            setMetricas(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error al cargar las métricas de contactos:', e);
        }
    }, [headers]);

    useEffect(() => { cargarMetricas(); }, [cargarMetricas]);

    // --- Modal de carga por partido -------------------------------------------

    const abrirModalCarga = async () => {
        setMensajeModal(null);
        setPartidoModal('');
        setContactosModal([]);
        setBaselineModal([]);
        setModalCarga(true);
        if (partidosTodos.length === 0) {
            try {
                const r = await fetch('/api/contactos-proyecto/partidos', { headers });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                setPartidosTodos(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error('Error al cargar los partidos:', e);
                setMensajeModal({ ok: false, texto: 'No se pudieron cargar los partidos' });
            }
        }
    };

    const elegirPartidoModal = async (partido) => {
        setPartidoModal(partido);
        setMensajeModal(null);
        setContactosModal([]);
        setBaselineModal([]);
        if (!partido) return;
        setCargandoModal(true);
        try {
            const qs = new URLSearchParams({ tipo_subsidio: tipoFiltro, partido }).toString();
            const r = await fetch(`/api/contactos-proyecto?${qs}`, { headers });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            const lista = Array.isArray(data) ? data : [];
            setContactosModal(lista);
            setBaselineModal(lista);
        } catch (e) {
            console.error('Error al cargar contactos del partido:', e);
            setMensajeModal({ ok: false, texto: 'No se pudieron cargar los contactos' });
        } finally {
            setCargandoModal(false);
        }
    };

    const guardarModal = async () => {
        if (!partidoModal) return;
        setGuardandoModal(true);
        setMensajeModal(null);
        try {
            // Mismo criterio que en la pagina: lo tipeado sin agregar se agrega
            // antes de guardar.
            let lista = contactosModal;
            const pendiente = await listaModalRef.current?.agregarPendiente?.();
            if (pendiente) {
                lista = [...contactosModal, pendiente];
                setContactosModal(lista);
            }

            // Mismo modal de impacto que en la pagina: el cg_12 de la obra es
            // una copia congelada y el cambio se ve recien al abrir y guardar
            // la obra.
            const cambios = await obrasDeContactosEditados(lista, baselineModal, {
                tipo_subsidio: tipoFiltro, partido: partidoModal
            });
            if (cambios.length > 0) {
                setImpactoPendiente({ lista, afectadas: cambios, enModal: true });
                return;
            }
            await ejecutarGuardadoModal(lista);
        } catch (e) {
            console.error('Error al preparar el guardado del modal:', e);
            setMensajeModal({ ok: false, texto: 'No se pudo guardar la lista de contactos' });
        } finally {
            setGuardandoModal(false);
        }
    };

    // El PUT de verdad del modal de partido, con la lista ya resuelta.
    const ejecutarGuardadoModal = async (lista) => {
        setGuardandoModal(true);
        try {
            const r = await fetch('/api/contactos-proyecto', {
                method: 'PUT',
                headers,
                body: JSON.stringify({
                    tipo_subsidio: tipoFiltro,
                    partido: partidoModal,
                    contactos: lista
                })
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            const guardadosModal = Array.isArray(data.contactos) ? data.contactos : [];
            setContactosModal(guardadosModal);
            setBaselineModal(guardadosModal);
            listaModalRef.current?.recargarCatalogo?.();

            const editados = data.editados || 0;
            const bloqueados = Array.isArray(data.bloqueados) ? data.bloqueados : [];
            const conflictos = Array.isArray(data.conflictos) ? data.conflictos : [];

            const partes = [];
            if (editados > 0) partes.push(`${editados} editado${editados > 1 ? 's' : ''}`);
            let texto = 'Contactos guardados.';
            if (partes.length > 0) texto = `Contactos guardados (${partes.join(', ')}).`;
            if (bloqueados.length > 0) {
                const detalle = bloqueados.map(b => {
                    const donde = b.obras > 1 ? `${b.obra} y ${b.obras - 1} más` : b.obra;
                    return `${b.nombre || `contacto ${b.contacto_id}`} (obra ${donde})`;
                }).join(', ');
                texto += ` No se pudo quitar ${bloqueados.length} porque ${bloqueados.length > 1 ? 'los usan obras' : 'lo usa una obra'}: ${detalle}.`;
            }
            if (conflictos.length > 0) {
                const detalle = conflictos.map(c =>
                    `${c.nombre || `contacto ${c.contacto_id}`} (igual a «${c.igualA}»)`
                ).join(', ');
                texto += ` No se pudo editar ${conflictos.length} porque ${conflictos.length > 1 ? 'ya existen otros con esos datos' : 'ya existe otro con esos datos'}: ${detalle}.`;
            }

            setMensajeModal({
                ok: true,
                advertencia: bloqueados.length > 0 || conflictos.length > 0,
                texto
            });

            // La lista y las metricas se refrescan: el partido puede aparecer
            // ahora aunque no tenga obras.
            cargarLista();
            cargarMetricas();
        } catch (e) {
            console.error('Error al guardar los contactos del modal:', e);
            setMensajeModal({ ok: false, texto: 'No se pudieron guardar los contactos' });
        } finally {
            setGuardandoModal(false);
        }
    };

    const cargarDetalle = useCallback(async (id) => {
        try {
            setError('');
            const r = await fetch(`/api/ongs/${id}`, { headers });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            const limpio = data && data.id ? data : null;
            setDetalle(limpio);
            setBorrador(Array.isArray(limpio?.contactos) ? limpio.contactos : []);
        } catch (e) {
            console.error('Error al cargar la ONG:', e);
            setError('No se pudo cargar la ONG seleccionada');
            setDetalle(null);
            setBorrador([]);
        } finally {
            setCargandoDetalle(false);
        }
    }, [headers]);

    const cargarObras = useCallback(async (id) => {
        setCargandoObras(true);
        try {
            const r = await fetch(`/api/ongs/${id}/obras`, { headers });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            setObras(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error al cargar las obras de la ONG:', e);
            setObras([]);
            setError('No se pudieron cargar las obras de la ONG');
        } finally {
            setCargandoObras(false);
        }
    }, [headers]);

    const cargarContactosPartido = useCallback(async (partido, tipo = tipoFiltro) => {
        try {
            setError('');
            const qs = new URLSearchParams({ tipo_subsidio: tipo, partido }).toString();
            const r = await fetch(`/api/contactos-proyecto?${qs}`, { headers });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            const lista = Array.isArray(data) ? data : [];
            setBorrador(lista);
            setBaselinePartido(lista);
        } catch (e) {
            console.error('Error al cargar los contactos del partido:', e);
            setError('No se pudieron cargar los contactos del partido');
            setBorrador([]);
            setBaselinePartido([]);
        } finally {
            setCargandoDetalle(false);
        }
    }, [headers, tipoFiltro]);

    const cargarObrasPartido = useCallback(async (partido, tipo = tipoFiltro) => {
        setCargandoObras(true);
        try {
            const qs = new URLSearchParams({ tipo_subsidio: tipo, partido }).toString();
            const r = await fetch(`/api/contactos-proyecto/obras?${qs}`, { headers });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            setObras(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error al cargar las obras del partido:', e);
            setObras([]);
            setError('No se pudieron cargar las obras del partido');
        } finally {
            setCargandoObras(false);
        }
    }, [headers, tipoFiltro]);

    // Doble click sobre una obra -> editarla en /inicio. Patrón idéntico al de
    // ChatBurbujaGlobal y TablaObras.
    const editarObra = (idPartidoObra) => {
        if (!idPartidoObra) return;
        setItem('idPartidoObra', idPartidoObra);
        setItem('isEditingPagos', 'false');
        // Para que al volver (guardar o cancelar) se abra el mismo panel y no
        // uno vacío. InicioPage lo lee y lo convierte en los query params.
        if (esModoONG && detalle) {
            setItem('ongIdOrigen', String(detalle.id));
        } else if (partidoSel) {
            setItem('partidoIdOrigen', `${tipoFiltro}|${partidoSel}`);
        }
        cambiarEstadoApp('edit_desde_pagos', {
            obra: idPartidoObra,
            origen: 'contactos_ong'
        });
        navigate(`/inicio?id_partido_obra=${encodeURIComponent(idPartidoObra)}`);
    };

    const seleccionar = (id) => {
        if (id === seleccionId) {
            setSeleccionId(null);
            setDetalle(null);
            setBorrador([]);
            setObras([]);
            setMensaje(null);
            setAvisoObras('');
            return;
        }
        setSeleccionId(id);
        setCargandoDetalle(true);
        setCargandoObras(true);
        setMensaje(null);
        setAvisoObras('');
        setDetalle(null);
        setBorrador([]);
        setObras([]);
        cargarDetalle(id);
        cargarObras(id);
    };

    const seleccionarPartido = (partido) => {
        if (partido === partidoSel) {
            setPartidoSel(null);
            setBorrador([]);
            setBaselinePartido([]);
            setObras([]);
            setMensaje(null);
            setAvisoObras('');
            return;
        }
        setPartidoSel(partido);
        setCargandoDetalle(true);
        setCargandoObras(true);
        setMensaje(null);
        setAvisoObras('');
        setBorrador([]);
        setBaselinePartido([]);
        setObras([]);
        cargarContactosPartido(partido);
        cargarObrasPartido(partido);
    };

    // Antes de guardar, preguntar al backend que obras tienen CARGADO los
    // contactos que cambiaron. Si hay alguna, se abre el modal de impacto: el
    // cg_12 de la obra es una copia congelada, asi que el cambio recien se ve
    // cuando se abra y guarde esa obra. Sin avisar, el usuario guarda, no ve
    // ningun cambio en la obra y cree que no se guardo.
    //
    // Se consulta SOLO por los contactos que cambiaron, no por toda la lista:
    // si no, editing un contacto sin uso avisaria de obras que no tienen nada que
    // ver.
    const prepararGuardado = async () => {
        setGuardando(true);
        setMensaje(null);
        try {
            // Si quedo algo tipeado sin apretar "Agregar contacto", se agrega
            // ahora: el guardado incluye ese contacto en vez de perderlo.
            let lista = borrador;
            const pendiente = await listaRef.current?.agregarPendiente?.();
            if (pendiente) {
                lista = [...borrador, pendiente];
                setBorrador(lista);
            }

            const base = esModoONG ? (detalle?.contactos ?? []) : baselinePartido;
            const cambios = await obrasDeContactosEditados(lista, base);
            if (cambios.length > 0) {
                // Todavia no se guardó: el modal decide.
                setImpactoPendiente({ lista, afectadas: cambios });
                return;
            }
            await ejecutarGuardado(lista);
        } catch (e) {
            console.error('Error al preparar el guardado:', e);
            setMensaje({ ok: false, texto: 'No se pudo guardar la lista de contactos' });
        } finally {
            setGuardando(false);
        }
    };

    // El PUT de verdad, con la lista ya resuelta. La puede llamar el guardado
    // normal o el botón "Guardar igual" del modal de impacto.
    const ejecutarGuardado = async (lista) => {
        setGuardando(true);
        try {
            const r = esModoONG
                ? await fetch(`/api/ongs/${seleccionId}/contactos`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ contactos: lista })
                })
                : await fetch('/api/contactos-proyecto', {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({
                        tipo_subsidio: tipoFiltro,
                        partido: partidoSel,
                        contactos: lista
                    })
                });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            const guardados = Array.isArray(data.contactos) ? data.contactos : [];
            setBorrador(guardados);
            // La linea base se actualiza: despues de guardar no hay cambios.
            if (!esModoONG) setBaselinePartido(guardados);
            // El catalogo del componente quedo viejo (altas y bajas recien
            // hechas): se recarga para que "disponibles" refleje el estado.
            listaRef.current?.recargarCatalogo?.();
            const bajas = data.dados_de_baja || 0;
            const editados = data.editados || 0;
            const bloqueados = Array.isArray(data.bloqueados) ? data.bloqueados : [];
            const conflictos = Array.isArray(data.conflictos) ? data.conflictos : [];

            const partes = [];
            if (editados > 0) partes.push(`${editados} contacto${editados > 1 ? 's' : ''} editado${editados > 1 ? 's' : ''}`);
            if (bajas > 0) partes.push(`${bajas} contacto${bajas > 1 ? 's' : ''} dado${bajas > 1 ? 's' : ''} de baja`);

            let texto = partes.length > 0 ? `Guardado: ${partes.join(' y ')}.` : 'Guardado.';

            // Los que se sacaron de la lista pero los usa una obra no se pueden
            // dar de baja: reaparecen. Se avisa cual y en que obra, si no parece
            // que el guardado no hizo caso.
            if (bloqueados.length > 0) {
                const detalle = bloqueados.map(b => {
                    const donde = b.obras > 1 ? `${b.obra} y ${b.obras - 1} más` : b.obra;
                    return `${b.nombre || `contacto ${b.contacto_id}`} (obra ${donde})`;
                }).join(', ');
                texto += bloqueados.length === 1
                    ? ` No se pudo quitar 1 contacto porque lo usa una obra: ${detalle}.`
                    : ` No se pudieron quitar ${bloqueados.length} contactos porque los usan obras: ${detalle}.`;
            }

            // Un contacto editado cuyos datos ya existen en OTRO contacto del
            // mismo ámbito: no se toca nada, queda como estaba.
            if (conflictos.length > 0) {
                const detalle = conflictos.map(c => `${c.nombre || `contacto ${c.contacto_id}`} (igual a «${c.igualA}»)`)
                    .join(', ');
                texto += conflictos.length === 1
                    ? ` No se pudo editar 1 contacto porque ya existe otro con esos datos: ${detalle}.`
                    : ` No se pudieron editar ${conflictos.length} contactos porque ya existen otros con esos datos: ${detalle}.`;
            }

            setMensaje({
                ok: true,
                advertencia: bloqueados.length > 0 || conflictos.length > 0,
                texto
            });
            if (esModoONG) {
                setDetalle(prev => (prev ? { ...prev, contactos: guardados } : prev));
                setOngs(prev => prev.map(o => (
                    o.id === seleccionId ? { ...o, contactos_count: guardados.length } : o
                )));
                // Las obras se recargan porque dar de baja un contacto puede
                // liberar otro, y la lista muestra cuáles contactos usa cada obra.
                cargarObras(seleccionId);
            } else {
                // El conteo del panel izquierdo cambia con la baja.
                cargarLista();
            }
            // Las métricas de la cabecera dependen de todos los tipos, no del que
            // está seleccionado: se refrescan siempre.
            cargarMetricas();
        } catch (e) {
            console.error('Error al guardar los contactos:', e);
            setMensaje({ ok: false, texto: 'No se pudo guardar la lista de contactos' });
        } finally {
            setGuardando(false);
        }
    };

    // "Guardar igual" del modal de impacto: usa la lista que ya estaba resuelta
    // cuando se preguntó, sin volver a preguntar. Reenvia al guardado del modal
    // de partido si el modal de impacto se abrio desde ahi.
    const confirmarGuardarImpacto = async () => {
        if (!impactoPendiente) return;
        const { lista, enModal } = impactoPendiente;
        setImpactoPendiente(null);
        if (enModal) await ejecutarGuardadoModal(lista);
        else await ejecutarGuardado(lista);
    };

    const cancelarGuardarImpacto = () => setImpactoPendiente(null);

    // Que contactos de la lista cambiaron de verdad, y de esos, cuales tienen
    // obras que los tienen cargados.
    //
    // Solo se pregunta por los que cambiaron: si se preguntara por toda la
    // lista, editar un contacto que no usa ninguna obra abriria el modal
    // avisando de obras que no tienen nada que ver.
    const obrasDeContactosEditados = async (lista, base, scopeAlterno) => {
        const basePorId = new Map((Array.isArray(base) ? base : [])
            .map(c => [c.contacto_id, c]));
        const texto = (v) => String(v ?? '').trim();
        const ids = (Array.isArray(lista) ? lista : [])
            .filter(c => {
                if (!c.contacto_id) return false; // todavia no existe en el catalogo
                const previo = basePorId.get(c.contacto_id);
                if (!previo) return false;
                return texto(previo.nombre) !== texto(c.nombre)
                    || texto(previo.telefono) !== texto(c.telefono)
                    || texto(previo.email) !== texto(c.email);
            })
            .map(c => c.contacto_id);
        if (ids.length === 0) return [];

        // El modal de partido consulta su propio scope, que puede no ser el que
        // tiene seleccionado el panel izquierdo.
        const esOng = !scopeAlterno;
        const qs = new URLSearchParams({ ids: ids.join(',') });
        const ruta = esOng
            ? `/api/ongs/${seleccionId}/obras-contactos?${qs}`
            : `/api/contactos-proyecto/obras-contactos?${new URLSearchParams({
                tipo_subsidio: scopeAlterno.tipo_subsidio,
                partido: scopeAlterno.partido,
                ids: ids.join(',')
            })}`;
        const r = await fetch(ruta, { headers });
        if (!r.ok) {
            // Si la consulta falla se guarda igual: el aviso es una ayuda, no
            // un motivo para impedir guardar.
            console.error('No se pudieron consultar las obras de los contactos:', r.status);
            return [];
        }
        const datos = await r.json();
        return Array.isArray(datos) ? datos.filter(a => (a?.obras?.length ?? 0) > 0) : [];
    };

    // Al cambiar el tipo, se limpia todo lo seleccionado: una ONG no aplica a
    // un partido y al revés.
    const cambiarTipo = (valor) => {
        setTipoFiltro(valor);
        setSeleccionId(null);
        setDetalle(null);
        setObras([]);
        setPartidoSel(null);
        setBorrador([]);
        setBaselinePartido([]);
        setMensaje(null);
        setAvisoObras('');
        setBusqueda('');
        setBusquedaContacto('');
        setFiltro('todas');
    };

    // Nombres de los contactos que coinciden con el buscador de contactos, por
    // id de ONG o por partido. Sirve para dos cosas: filtrar (si la lista de
    // contactos esta vacia, no hay nada que matchear) y para que la fila diga
    // POR QUE aparece, que si no el usuario cree que filtro mal.
    const coincidencias = useMemo(() => {
        const t = busquedaContacto.trim();
        const mapa = new Map();
        if (t === '') return mapa;
        // Si el contacto no tiene nombre (son los que la migracion dejo con
        // solo el email, 43 de 635), se muestra el canal: un label vacio hace
        // que la fila aparezca sin explicacion.
        const etiqueta = (c) => {
            const nombre = (c.nombre || '').trim();
            if (nombre !== '') return nombre;
            const canales = [c.telefono, c.email].map(x => (x || '').trim()).filter(x => x !== '');
            return canales.length > 0 ? canales.join(' / ') : '(sin datos)';
        };
        if (esModoONG) {
            for (const o of ongs) {
                const nombres = (o.contactos || [])
                    .filter(c => coincideContacto(c, t))
                    .map(etiqueta);
                if (nombres.length > 0) mapa.set(o.id, nombres);
            }
        } else {
            for (const p of partidos) {
                const nombres = (p.contactos_lista || [])
                    .filter(c => coincideContacto(c, t))
                    .map(etiqueta);
                if (nombres.length > 0) mapa.set(p.partido, nombres);
            }
        }
        return mapa;
    }, [ongs, partidos, busquedaContacto, esModoONG]);

    const filtradas = useMemo(() => {
        const t = busqueda.trim().toLowerCase();
        const tc = busquedaContacto.trim();
        return ongs.filter(o => {
            if (filtro === 'con' && !(o.contactos_count > 0)) return false;
            if (filtro === 'sin' && o.contactos_count > 0) return false;
            if (tc !== '' && !coincidencias.has(o.id)) return false;
            if (t === '') return true;
            return (o.nombre || '').toLowerCase().includes(t)
                || (o.resolucion || '').toString().toLowerCase().includes(t)
                || (o.codigo || '').toString().toLowerCase().includes(t);
        });
    }, [ongs, busqueda, filtro, busquedaContacto, coincidencias]);

    const partidosFiltrados = useMemo(() => {
        const t = busqueda.trim().toLowerCase();
        const tc = busquedaContacto.trim();
        return partidos.filter(p => {
            if (filtro === 'con' && !(p.contactos > 0)) return false;
            if (filtro === 'sin' && p.contactos > 0) return false;
            if (tc !== '' && !coincidencias.has(p.partido)) return false;
            if (t === '') return true;
            return (p.partido || '').toLowerCase().includes(t);
        });
    }, [partidos, busqueda, filtro, busquedaContacto, coincidencias]);

    // Linea base de lo guardado: en ONG la trae detalle.contactos (viene del
    // servidor), en partido la guarda baselinePartido al cargar y al guardar.
    const baselineGuardado = esModoONG ? detalle?.contactos : baselinePartido;
    const hayCambios = (esModoONG ? Boolean(detalle) : Boolean(partidoSel))
        ? firmaContactos(borrador) !== firmaContactos(baselineGuardado)
        : false;

    // Se habilita guardar cuando hay algo que guardar: cambios en la lista, o
    // datos tipeados sin agregar (el guardado los agrega solo, asi el boton
    // habilitado no pierde el contacto). Sin nada de eso queda deshabilitado,
    // igual que "Agregar contacto".
    // Solo cuando la lista cambio (agrego o quito). Lo tipeado sin agregar
    // no alcanza: para eso esta "Agregar contacto".
    const puedeGuardar = hayCambios;

    // Mismo criterio para el modal: habilitado cuando hay algo que guardar.
    const puedeGuardarModal = Boolean(partidoModal)
        && firmaContactos(contactosModal) !== firmaContactos(baselineModal);
    const obrasConContactos = obras.filter(o => o.contactos.length > 0).length;

    // Lista de obras del scope con los contactos que usa cada una. La usan los
    // dos modos: en ONG son las obras de la ONG, en partido las del tipo y
    // partido. Es lo que explica por que un contacto no se puede dar de baja.
    const bloqueObras = (titulo, mensajeVacio) => {
        const mostradas = soloConContactos ? obras.filter(o => o.contactos.length > 0) : obras;
        return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {titulo} ({mostradas.length})
                    {obras.length > 0 && (
                        <span className="font-normal text-gray-500 dark:text-gray-400">
                            {' '}· {obrasConContactos} con contactos · doble click para editar
                        </span>
                    )}
                </span>
                {obras.length > 0 && (
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={soloConContactos}
                            onChange={(e) => setSoloConContactos(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                        />
                        Solo con contactos
                    </label>
                )}
            </div>
            {cargandoObras ? (
                <p className="p-4 text-sm text-gray-500">Cargando obras...</p>
            ) : mostradas.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 italic">
                    {obras.length === 0
                        ? mensajeVacio
                        : 'Ninguna de las obras listadas tiene contactos.'}
                </p>
            ) : (
                <>
                    {avisoObras && (
                        <p className="px-4 py-2 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-b border-gray-200 dark:border-gray-700">
                            {avisoObras}
                        </p>
                    )}
                    <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                        {mostradas.map(o => (
                            <li
                                key={o.id}
                                onDoubleClick={() => editarObra(o.obra)}
                                title="Doble click para editar la obra"
                                className="px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm text-gray-800 dark:text-gray-100 font-medium">
                                        {o.obra}
                                    </span>
                                    <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                        {o.estado || '—'}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {o.nombre || '—'}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-1">
                                    {o.contactos.length === 0 ? (
                                        <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                                            sin contactos
                                        </span>
                                    ) : (
                                        o.contactos.map(c => (
                                            <span
                                                key={c.contacto_id}
                                                className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200"
                                            >
                                                {c.nombre || `contacto ${c.contacto_id}`}
                                            </span>
                                        ))
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
    };

    return (
        <div className="p-4">
            <div className="flex flex-col p-4 mb-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                    Contactos de proyecto
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {esModoONG
                        ? 'Cada ONG tiene su catálogo de contactos. Cuando una obra elige un contacto que no estaba, queda cargado en el catálogo de esa ONG y disponible para las demás obras de la misma ONG. Acá se edita la lista de contactos; los datos de la ONG se muestran como referencia y se editan en Nueva ONG.'
                        : 'Los contactos de Municipio, Universidades y Otro se agrupan por tipo de subsidio y partido. Una obra ve los contactos de su mismo tipo y su mismo partido. Acá se edita la lista de cada partido.'}
                </p>
            </div>

            {/* Métricas por tipo. Cada fila cuenta SU unidad: ONGs para ONG, y
                partidos con obras de ese tipo para los demás. Click en la fila
                cambia el tipo seleccionado. */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300">
                            <th className="text-left font-medium px-4 py-2">Tipo de subsidio</th>
                            <th className="text-right font-medium px-4 py-2">Total</th>
                            <th className="text-right font-medium px-4 py-2">Con contactos</th>
                            <th className="text-right font-medium px-4 py-2">Sin contactos</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {metricas.length === 0 && (
                            <tr><td colSpan={4} className="px-4 py-3 text-gray-500">Cargando métricas...</td></tr>
                        )}
                        {metricas.map(m => (
                            <tr
                                key={m.tipo}
                                onClick={() => cambiarTipo(m.tipo)}
                                className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 ${tipoFiltro === m.tipo ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                                title={`Ver los contactos de ${etiquetaTipo(m.tipo)}`}
                            >
                                <td className="px-4 py-2 text-gray-800 dark:text-gray-100">{etiquetaTipo(m.tipo)}</td>
                                <td className="px-4 py-2 text-right font-semibold text-gray-800 dark:text-gray-100">{m.total}</td>
                                <td className="px-4 py-2 text-right text-green-700 dark:text-green-400">{m.con_contactos}</td>
                                <td className={`px-4 py-2 text-right ${m.sin_contactos > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>{m.sin_contactos}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col md:flex-row gap-3 mb-4">
                <div className="md:w-64">
                    <Select_simple
                        name="tipoFiltro"
                        options={OPCIONES_TIPO}
                        value={tipoFiltro}
                        placeholder="Seleccionar tipo de subsidio..."
                        onChange={(e) => cambiarTipo(normalizarTipoSubsidio(e.target.value) || '')}
                    />
                </div>
                {hayTipo && !esModoONG && (
                    <div className="flex items-center">
                        <button
                            type="button"
                            onClick={abrirModalCarga}
                            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md whitespace-nowrap"
                            title="Cargar contactos de un partido, tenga o no obras de este tipo"
                        >
                            + Cargar contactos de un partido
                        </button>
                    </div>
                )}
                {/* El buscador y el filtro con/sin aparecen recien con un tipo
                    elegido: sin tipo no hay lista que buscar ni filtrar. */}
                {hayTipo && (
                    <>
                        {/* Los dos buscadores van en la misma fila y a la misma
                            anchura: el grid hace que cada uno tome la mitad y los
                            dos usan el mismo Input, asi que se ven iguales sin
                            tener que igualar clases a mano. */}
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                                type="text"
                                placeholder={esModoONG
                                    ? 'Buscar ONG por nombre, resolución o código...'
                                    : 'Buscar partido...'}
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                            />
                            <Input
                                type="text"
                                placeholder="Buscar contacto por nombre, teléfono o email..."
                                value={busquedaContacto}
                                onChange={(e) => setBusquedaContacto(e.target.value)}
                            />
                            {busquedaContacto.trim() !== '' && (
                                <p className="text-xs text-gray-500 md:col-span-2">
                                    Filtrando por los contactos que coinciden con «{busquedaContacto.trim()}».
                                    Si también escribís en el otro buscador, se filtra por los dos.
                                </p>
                            )}
                        </div>
                        <div className="md:w-56">
                            <Select_simple
                                name="filtroContactos"
                                options={[
                                    { value: 'todas', label: esModoONG ? 'Todas las ONGs' : 'Todos los partidos' },
                                    { value: 'con', label: 'Solo con contactos' },
                                    { value: 'sin', label: 'Solo sin contactos' }
                                ]}
                                value={filtro}
                                onChange={(e) => setFiltro(e.target.value)}
                            />
                        </div>
                    </>
                )}
            </div>

            {error && (
                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
            )}

            {!hayTipo && (
                <p className="text-sm text-gray-500 italic">
                    Elegí un tipo de subsidio —en el selector o en la tabla de arriba— para ver sus contactos.
                </p>
            )}

            {hayTipo && (

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200">
                        {esModoONG ? `ONGs (${filtradas.length})` : `Partidos (${partidosFiltrados.length})`}
                    </div>
                    <ul className="max-h-[32rem] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                        {cargando && <li className="p-4 text-sm text-gray-500">Cargando...</li>}
                        {!cargando && esModoONG && filtradas.length === 0 && (
                            <li className="p-4 text-sm text-gray-500 italic">Ninguna ONG coincide.</li>
                        )}
                        {!cargando && !esModoONG && partidosFiltrados.length === 0 && (
                            <li className="p-4 text-sm text-gray-500 italic">
                                Ningún partido coincide.
                            </li>
                        )}
                        {esModoONG && filtradas.map(o => (
                            <li key={o.id}>
                                <button
                                    type="button"
                                    onClick={() => seleccionar(o.id)}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center justify-between gap-2 ${seleccionId === o.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                                >
                                    <span className="min-w-0">
                                        <span className="block text-sm text-gray-800 dark:text-gray-100 truncate">{o.nombre}</span>
                                        <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                            Res: {o.resolucion || '—'} · Cód: {o.codigo || '—'}
                                        </span>
                                        {coincidencias.has(o.id) && (
                                            <span className="block text-xs text-blue-600 dark:text-blue-400 truncate">
                                                Contacto: {coincidencias.get(o.id).join(', ')}
                                            </span>
                                        )}
                                    </span>
                                    <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${o.contactos_count > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
                                        {o.contactos_count}
                                    </span>
                                </button>
                            </li>
                        ))}
                        {!esModoONG && partidosFiltrados.map(p => (
                            <li key={p.partido}>
                                <button
                                    type="button"
                                    onClick={() => seleccionarPartido(p.partido)}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center justify-between gap-2 ${partidoSel === p.partido ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                                >
                                    <span className="min-w-0">
                                        <span className="block text-sm text-gray-800 dark:text-gray-100 truncate">{p.partido}</span>
                                        {coincidencias.has(p.partido) && (
                                            <span className="block text-xs text-blue-600 dark:text-blue-400 truncate">
                                                Contacto: {coincidencias.get(p.partido).join(', ')}
                                            </span>
                                        )}
                                    </span>
                                    <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${p.contactos > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
                                        {p.contactos}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="space-y-4">
                    {cargandoDetalle ? (
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            <p className="text-sm text-gray-500">Cargando...</p>
                        </div>
                    ) : esModoONG && detalle ? (
                        <>
                            {/* Referencia. No se edita acá. */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                <div className="flex items-start justify-between gap-2 mb-3">
                                    <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 min-w-0">
                                        {detalle.nombre}
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => seleccionar(detalle.id)}
                                        className="shrink-0 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                    >
                                        Cerrar
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    Datos de la organización, solo a modo de referencia. Se editan en
                                    Nueva ONG.
                                </p>
                                <dl className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    <Dato etiqueta="Código" valor={detalle.codigo} />
                                    <Dato etiqueta="Resolución" valor={detalle.resolucion} />
                                    <Dato etiqueta="Partido" valor={detalle.partido_nombre} />
                                    <Dato etiqueta="Categoría" valor={detalle.categoria} />
                                    <Dato etiqueta="LPO" valor={detalle.lpo} />
                                    <Dato etiqueta="Agrupación / Terminal Política" valor={detalle.ref_1_agrupacion_terminal_politica} />
                                    <Dato etiqueta="Referente Político" valor={detalle.ref_2_referente_politico} />
                                    <Dato
                                        etiqueta="Municipios"
                                        valor={Array.isArray(detalle.municipios_seleccionados_nombres) && detalle.municipios_seleccionados_nombres.length > 0
                                            ? detalle.municipios_seleccionados_nombres.join(', ')
                                            : ''}
                                    />
                                </dl>
                            </div>

                            {/* Lo único que se edita en esta página. */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                <ListaContactosProyecto
                                    ref={listaRef}
                                    scope={{ tipo: 'ong', ongId: detalle.id }}
                                    contactos={borrador}
                                    onChange={setBorrador}
                                    etiquetaTitulo="Contactos de la organización"
                                    mensajeVacio="No hay contactos cargados para esta organización."
                                />
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {hayCambios ? 'Hay cambios sin guardar.' : 'Sin cambios sin guardar.'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={prepararGuardado}
                                        disabled={!puedeGuardar || guardando}
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-md"
                                    >
                                        {guardando ? 'Guardando...' : 'Guardar contactos'}
                                    </button>
                                </div>
                                {mensaje && (
                                    <p className={`mt-2 text-xs ${!mensaje.ok ? 'text-red-600 dark:text-red-400' : mensaje.advertencia ? 'text-amber-700 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                                        {mensaje.texto}
                                    </p>
                                )}
                            </div>

                            {/* De qué depende poder dar de baja un contacto. */}
                            {bloqueObras('Obras de esta ONG', 'Esta ONG no tiene obras asociadas.')}
                        </>
                    ) : !esModoONG && partidoSel ? (
                        <>
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 min-w-0">
                                        {partidoSel}
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => seleccionarPartido(partidoSel)}
                                        className="shrink-0 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                    >
                                        Cerrar
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    {tipoFiltro} · los contactos se comparten entre las obras de este tipo y partido.
                                </p>
                                <ListaContactosProyecto
                                    ref={listaRef}
                                    scope={{ tipo: 'partido', tipoSubsidio: tipoFiltro, partido: partidoSel }}
                                    contactos={borrador}
                                    onChange={setBorrador}
                                    etiquetaTitulo="Contactos del partido"
                                    mensajeVacio={`No hay contactos cargados para ${nombreScope(tipoFiltro)}.`}
                                />
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end">
                                    <button
                                        type="button"
                                        onClick={prepararGuardado}
                                        disabled={!puedeGuardar || guardando}
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-md"
                                    >
                                        {guardando ? 'Guardando...' : 'Guardar contactos'}
                                    </button>
                                </div>
                                {mensaje && (
                                    <p className={`mt-2 text-xs ${!mensaje.ok ? 'text-red-600 dark:text-red-400' : mensaje.advertencia ? 'text-amber-700 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                                        {mensaje.texto}
                                    </p>
                                )}
                            </div>

                            {bloqueObras('Obras de este partido', 'Este partido no tiene obras de este tipo.')}
                        </>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            <p className="text-sm text-gray-500 italic">
                                {esModoONG
                                    ? 'Elegí una ONG de la lista para ver sus datos, administrar sus contactos y ver en qué obras se usan.'
                                    : 'Elegí un partido de la lista para administrar sus contactos.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Modal de carga por partido. Permite cargar contactos de
                cualquier partido, aunque no tenga obras de este tipo, que es
                justo lo que la lista izquierda no cubre. */}
            <Modal
                isOpen={modalCarga}
                onClose={() => setModalCarga(false)}
                title={`Cargar contactos — ${etiquetaTipo(tipoFiltro)}`}
            >
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="partido_modal">Partido</Label>
                        <Select_simple
                            id="partido_modal"
                            name="partido_modal"
                            options={partidosTodos.map(p => ({ value: p.partido, label: p.partido }))}
                            value={partidoModal}
                            placeholder="Seleccionar partido..."
                            onChange={(e) => elegirPartidoModal(e.target.value)}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            La lista son todos los partidos, no solo los que tienen obras de {etiquetaTipo(tipoFiltro)}.
                        </p>
                    </div>

                    {partidoModal && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                            {cargandoModal ? (
                                <p className="text-sm text-gray-500">Cargando contactos...</p>
                            ) : (
                                <ListaContactosProyecto
                                    ref={listaModalRef}
                                    scope={{ tipo: 'partido', tipoSubsidio: tipoFiltro, partido: partidoModal }}
                                    contactos={contactosModal}
                                    onChange={setContactosModal}
                                    etiquetaTitulo="Contactos del partido"
                                    mensajeVacio={`No hay contactos cargados para ${nombreScope(tipoFiltro)}.`}
                                />
                            )}
                        </div>
                    )}

                    {mensajeModal && (
                        <p className={`text-xs ${!mensajeModal.ok ? 'text-red-600 dark:text-red-400' : mensajeModal.advertencia ? 'text-amber-700 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                            {mensajeModal.texto}
                        </p>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={() => setModalCarga(false)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                        >
                            Cerrar
                        </button>
                        <button
                            type="button"
                            onClick={guardarModal}
                            disabled={!puedeGuardarModal || guardandoModal}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-md"
                        >
                            {guardandoModal ? 'Guardando...' : 'Guardar contactos'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Aviso de impacto: se abre ANTES del PUT, para poder cancelar. El
                cg_12 de cada obra es una copia congelada del dato del contacto,
                asi que guardar el catalogo no cambia lo que la obra muestra. */}
            {impactoPendiente && (
                <Modal
                    isOpen={true}
                    onClose={cancelarGuardarImpacto}
                    title="El cambio se va a ver recién en esas obras"
                >
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                        Estás por cambiar {impactoPendiente.afectadas.length === 1 ? 'un contacto' : `${impactoPendiente.afectadas.length} contactos`}
                        {' '}que {impactoPendiente.afectadas.length === 1 ? 'tiene' : 'tienen'} obras cargadas.
                        El contacto de esas obras es una copia del dato actual: el cambio{' '}
                        <strong>no se ve todavía</strong>, va a aparecer cuando abras cada obra y la
                        guardes.
                    </p>
                    <ul className="mt-3 space-y-2">
                        {impactoPendiente.afectadas.map(a => (
                            <li
                                key={a.contacto_id}
                                className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2"
                            >
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {etiquetaContactoAfectado(a)}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {(Array.isArray(a.obras) ? a.obras : []).map(codigo => (
                                        <span
                                            key={codigo}
                                            className="rounded bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200"
                                        >
                                            {codigo}
                                        </span>
                                    ))}
                                </div>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                        El catálogo se guarda igual. Para que una obra tome el dato nuevo hay que
                        entrar a esa obra y guardarla.
                    </p>
                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={cancelarGuardarImpacto}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={confirmarGuardarImpacto}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                        >
                            Guardar igual
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ContactosONGPage;
